import React, { useEffect, useState } from "react";
import { FlaskConical, Eye, EyeOff, Info } from "lucide-react";
import api from "@/lib/api";
import ChartEngine from "@/components/ChartEngine";

const OFF = { volume: false, vwap: false, ema9: false, ema20: false, ema50: false, ema200: false, rsi: false };

export default function IndicatorLab() {
  const [data, setData] = useState(null);
  const [candles, setCandles] = useState(null);
  const [toggles, setToggles] = useState({ ...OFF, volume: true });
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/indicator-lab").then((r) => setData(r.data)).catch(() => {});
    (async () => {
      try {
        const a = await api.get("/training/adaptive-next?stage=3");
        if (a.data.scenario_id) {
          const s = await api.post("/replay/start", { session_id: a.data.scenario_id });
          setCandles(s.data.candles);
        }
      } catch (e) { /* lab still shows content without a chart */ }
    })();
  }, []);

  const tryIt = (key) => {
    setToggles({ ...OFF, volume: true, [key]: true });
    setActive(key);
  };

  if (!data) return <div className="p-8 text-tc-muted font-mono text-sm">Loading Indicator Lab…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="indicator-lab">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 rounded-lg bg-tc-coach/15 border border-tc-coach/30 text-tc-coach flex items-center justify-center"><FlaskConical className="w-5 h-5" /></div>
        <h1 className="font-heading text-2xl font-bold">Indicator Lab</h1>
      </div>
      <div className="flex items-start gap-2 text-[13px] text-tc-coach bg-tc-coach/10 border border-tc-coach/20 rounded-lg px-3 py-2 mt-3">
        <Info className="w-4 h-4 mt-0.5 shrink-0" /> <span className="font-mono tracking-wide">{data.principle}</span>
      </div>

      {candles && (
        <div className="mt-4">
          <ChartEngine candles={candles} toggles={toggles} symbol="BTC/USD" interval="5m" height={320} hideHeader rightOffset={6} />
          {active && <div className="text-[11px] font-mono text-tc-muted mt-1">Showing: {active.toUpperCase()} on a real classified market.</div>}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mt-5">
        {data.indicators.map((ind) => (
          <div key={ind.key} className="tc-card p-5" data-testid={`lab-${ind.key}`}>
            <div className="flex items-center justify-between">
              <div className="font-heading font-bold text-lg" style={{ color: ind.color }}>{ind.name}</div>
              <button data-testid={`lab-try-${ind.key}`} onClick={() => tryIt(ind.toggle)}
                className={`text-[11px] font-mono px-2.5 py-1 rounded-md border flex items-center gap-1.5 ${active === ind.toggle ? "bg-tc-coach/20 text-tc-coach border-tc-coach/50" : "bg-white/5 text-tc-sub border-white/10 hover:border-white/30"}`}>
                {active === ind.toggle ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Try it
              </button>
            </div>
            <div className="mt-3 space-y-2.5 text-[13px]">
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-tc-bull">What it measures</div><p className="text-tc-sub">{ind.measures}</p></div>
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted">What it does NOT tell you</div><p className="text-tc-sub">{ind.not}</p></div>
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-tc-bear">Common misuse</div><p className="text-tc-sub">{ind.misuse}</p></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
